import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { MoContext } from 'contexts/MoContext';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface MoSpecialRequestModalProps {
  segment?: FullSegment;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (userCode: number) => void;
  user: any;
}

const MoChangeUserModal: FC<MoSpecialRequestModalProps> = ({
  segment,
  openModal,
  setOpenModal,
  user,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const { modelOptions } = useContext(MoContext);
  const [choosenDep, setChoosenDep] = useState<Select>();
  const [choosenBranch, setChoosenBranch] = useState<Select>();
  const [choosenUser, setChoosenUser] = useState<Select>();

  const [newDepartmentId, setNewDepartmentId] = useState<number>(-1);
  const [newBranchId, setNewBranchId] = useState<number>(-1);
  const [newUserCode, setNewUserCode] = useState<number>(-1);
  const [filteredBranches, setFilteredBranches] = useState(modelOptions?.branchesOptions || []);
  const [filteredUsers, setFilteredUsers] = useState(modelOptions?.usersOptions || []);

  useEffect(() => {
    if (openModal) {
      const choosenDep = modelOptions?.departmentsOptions?.find(
        (dep) => dep.value === user.departmentId + ''
      );
      setChoosenDep(choosenDep);

      const choosenBranch = modelOptions?.branchesOptions?.find(
        (branch) => branch.value === user.branchId + ''
      );
      setChoosenBranch(choosenBranch);

      const choosenUser = modelOptions?.usersOptions?.find((u) => u.value === user.user_code + '');
      setChoosenUser(choosenUser);

      setNewDepartmentId(user.departmentId);
      setNewBranchId(user.branchId);
      setNewUserCode(user.user_code);

      if (user.departmentId) {
        const relatedBranches =
          modelOptions?.branchesOptions
            .filter((br) => br.value !== '-1')
            .map((br) => {
              return {
                ...br,
                data: {
                  ...br.data,
                  visible: br.data?.departmentId === user.departmentId,
                },
              };
            }) || [];
        setFilteredBranches(relatedBranches);

        const relatedUsers =
          modelOptions?.usersOptions
            .filter((br) => br.value !== '-1')
            .map((u) => ({
              ...u,
              data: {
                ...u.data,
                visible:
                  u.data?.departmentId === user.departmentId && u.data?.branchId === user.branchId,
              },
            })) || [];
        setFilteredUsers(relatedUsers);
      } else {
        setFilteredBranches(modelOptions?.branchesOptions || []);
        setFilteredUsers(modelOptions?.usersOptions || []);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const onSelectDepartment = (departmentId: string) => {
    const deptId = +departmentId;
    setNewDepartmentId(deptId);

    const relatedBranches =
      modelOptions?.branchesOptions
        .filter((br) => br.value !== '-1')
        .map((br) => {
          return {
            ...br,
            data: {
              ...br.data,
              visible:
                br.data?.departmentId === deptId || br.data?.departmentId === -1 || deptId === -1,
            },
          };
        }) || [];

    const relatedUsers =
      modelOptions?.usersOptions
        .filter((br) => br.value !== '-1')
        .map((u) => ({
          ...u,
          data: {
            ...u.data,
            visible:
              u.data?.departmentId === deptId || u.data?.departmentId === -1 || deptId === -1,
          },
        })) || [];

    setFilteredBranches(relatedBranches);
    setFilteredUsers(relatedUsers);

    setNewBranchId(-1);
    setNewUserCode(-1);
  };

  const onSelectBranch = (branchId: string) => {
    const brId = +branchId;
    setNewBranchId(brId);

    const relatedUsers =
      modelOptions?.usersOptions
        .filter((br) => br.value !== '-1')
        .map((u) => ({
          ...u,
          data: {
            ...u.data,
            visible: u.data?.departmentId === newDepartmentId && u.data?.branchId === brId,
          },
        })) || [];

    setFilteredUsers(relatedUsers);

    setNewUserCode(-1);
  };

  return createPortal(
    <div style={{ direction: 'ltr' }}
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable"
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' modal'} style={modalStyle}>
        <div
          className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            {t('mo.change_user')}{' '}
            {segment && (
              <Fragment>
                {' '}-{' '}Order: {segment.orderId}{' '}
                Segment:
                {segment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(segment.roomsStatusCode)}`}>
                  {segment.roomsStatusCode}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>

        <div className="modal-body text-xsm mo-message-modal-body">
          <div className="mo-cu-block">
            <div className="title text-st text-st">{t('mo.current_user')}</div>
            <div>
              <div className="mb-20"></div>
              <SelectCustom
                value={choosenDep?.value || ''}
                onChange={() => { }}
                disabled={true}
                options={modelOptions?.departmentsOptions || []}
                placeholder={t('mo.department')}
                showHtmlPrev={true}
                movablePlaceholder={t('mo.department')}
                menuPosition="fixed"
              />
              <div className="mb-20"></div>

              <SelectCustom
                value={choosenBranch?.value || ''}
                onChange={() => { }}
                disabled={true}
                options={modelOptions?.branchesOptions || []}
                placeholder={t('mo.branch')}
                showHtmlPrev={true}
                movablePlaceholder={t('mo.branch')}
                menuPosition="fixed"
              />
              <div className="mb-20"></div>

              <SelectCustom
                value={choosenUser?.value || ''}
                onChange={() => { }}
                disabled={true}
                options={modelOptions?.usersOptions || []}
                placeholder={t('mo.user')}
                showHtmlPrev={true}
                movablePlaceholder={t('mo.user')}
                menuPosition="fixed"
              />
            </div>
          </div>
          <div className="mo-cu-block">
            <div className="title text-st success">{t('mo.change_user')}</div>
            <div>
              <div className="mb-20"></div>
              <div className="mb-20">
                <SelectCustom
                  onChange={onSelectDepartment}
                  value={newDepartmentId + ''}
                  options={modelOptions?.departmentsOptions.filter((d) => d.value !== '-1') || []}
                  placeholder={t('mo.department')}
                  showHtmlPrev={true}
                  movablePlaceholder={t('mo.department')}
                  menuPosition="fixed"
                  menuPortralClassName="mo-foa"
                />
              </div>
              <div className="mb-20">
                <SelectCustom
                  onChange={onSelectBranch}
                  value={newBranchId + ''}
                  options={filteredBranches.filter((br) => br.data?.visible)}
                  placeholder={t('mo.branch')}
                  showHtmlPrev={true}
                  movablePlaceholder={t('mo.branch')}
                  menuPosition="fixed"
                  menuPortralClassName="mo-foa"
                  menuPlacement="top"
                />
              </div>
              <div className="mb-20">
                <SelectCustom
                  onChange={(userCode) => setNewUserCode(+userCode)}
                  value={newUserCode + ''}
                  options={filteredUsers.filter((u) => u.data?.visible)}
                  placeholder={t('mo.user')}
                  showHtmlPrev={true}
                  movablePlaceholder={t('mo.user')}
                  menuPosition="fixed"
                  menuPortralClassName="mo-foa"
                  menuPlacement="top"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button
            title={t('save')}
            className="btn btn-primary ml-0 mr-a"
            onClick={() => props.onSubmit(newUserCode)}
            disabled={newUserCode === -1}
          />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
      </div>
    </div>
    , document.body);
};

export default withErrorBoundary(MoChangeUserModal, 'MoChangeUserModal');
